import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Loader2, CheckCircle2, Trash2, Sparkles, Building2, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const SECTION_LABELS = {
  duties: 'Duties & Responsibilities',
  event_briefing: 'Event Briefing',
  performance_expectations: 'Performance Expectations',
  reporting_requirements: 'Reporting Requirements',
  required_photos: 'Required Photos / Evidence',
  dress_code: 'Dress Code & Appearance',
  parking_logistics: 'Parking & Logistics',
  task_checklist: 'Task Checklist',
};

const renderValue = (v) => {
  if (Array.isArray(v)) return v.length ? (
    <ul className="list-disc pl-5 space-y-0.5">{v.map((i, idx) => <li key={idx}>{i}</li>)}</ul>
  ) : <span className="text-gray-400">—</span>;
  return v ? <span>{v}</span> : <span className="text-gray-400">—</span>;
};

const AdminRequirementApprovals = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/requirement-drafts/pending');
      setDrafts(res.data.drafts || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load pending requirements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (draftId, action) => {
    setBusyId(draftId);
    try {
      await api.post(`/admin/requirement-drafts/${draftId}/${action}`);
      toast.success(action === 'approve' ? 'Approved — now visible to talent.' : 'Draft discarded.');
      setDrafts((p) => p.filter((d) => d.draft_id !== draftId));
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${action}.`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-requirement-approvals-page">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-6 w-6 text-indigo-600" />
          <h1 className="text-2xl font-bold text-gray-900">Requirement Approvals</h1>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          AI-organized requirements submitted by clients and agencies. Approve to make them visible to talent, or discard.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 py-10"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
        ) : drafts.length === 0 ? (
          <Card data-testid="no-pending"><CardContent className="py-10 text-center text-gray-500">No requirements awaiting approval.</CardContent></Card>
        ) : (
          <div className="space-y-5">
            {drafts.map((d) => (
              <Card key={d.draft_id} data-testid={`pending-draft-${d.draft_id}`}>
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {d.entity_type === 'agency_job' ? <Building2 className="h-4 w-4 text-purple-600" /> : <Briefcase className="h-4 w-4 text-cyan-600" />}
                      {d.job_title || 'Untitled job'}
                    </CardTitle>
                    <Badge className="bg-blue-100 text-blue-800">
                      {d.entity_type === 'agency_job' ? 'Agency' : 'Client'} • {d.created_by_role}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    {Object.keys(SECTION_LABELS).map((key) => {
                      const val = (d.sections || {})[key];
                      if (!val || (Array.isArray(val) && val.length === 0)) return null;
                      return (
                        <div key={key}>
                          <div className="font-semibold text-gray-700">{SECTION_LABELS[key]}</div>
                          <div className="text-gray-600">{renderValue(val)}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-5">
                    <Button onClick={() => act(d.draft_id, 'approve')} disabled={busyId === d.draft_id}
                      className="bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-btn-${d.draft_id}`}>
                      {busyId === d.draft_id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                      Approve
                    </Button>
                    <Button onClick={() => act(d.draft_id, 'discard')} disabled={busyId === d.draft_id}
                      variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`discard-btn-${d.draft_id}`}>
                      <Trash2 className="h-4 w-4 mr-2" /> Discard
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRequirementApprovals;
